import { useEffect, useMemo, useState } from "react";
import { Elements, PaymentElement, ExpressCheckoutElement,
         useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { api } from "@/lib/api";
import TermsAcceptance from "@/components/TermsAcceptance";

let _stripePromise = null;
const getStripe = (pk) => {
  if (!pk) return null;
  if (!_stripePromise) _stripePromise = loadStripe(pk);
  return _stripePromise;
};

function Inner({ providerIntentId, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const finish = async () => {
    // The acceptance was already stamped on the payment row when the user
    // ticked the checkbox at the top of this screen (see
    // TermsAcceptance below). We still resend the flag so the server can
    // enforce it at the /payments/confirm gate and refuse finalisation if
    // it is missing — defense-in-depth against a modified client.
    const r = await api.post("/payments/confirm", {
      provider_intent_id: providerIntentId,
      accepted_terms: true,
    });
    if (r.data.state === "succeeded") { onDone(r.data.message); return true; }
    if (r.data.state === "failed") { setErr("Payment failed. You have not been charged."); }
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr("");
    const { error } = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (error) { setErr(error.message || "Payment failed"); setBusy(false); return; }
    await finish();
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="publish-payment-form">
      <ExpressCheckoutElement onConfirm={submit}
                              options={{ paymentMethods: { applePay: "auto", googlePay: "auto" } }} />
      <div className="oh-label text-[color:var(--oh-ink-3)] text-center">or pay with card</div>
      <PaymentElement options={{ layout: "tabs" }} />
      {err && <div className="text-sm text-[color:var(--oh-red)]" data-testid="publish-payment-error">{err}</div>}
      <button type="submit" disabled={!stripe || busy}
              className="btn-red w-full" data-testid="publish-pay">
        {busy ? "Processing…" : "Pay €1 · Publish"}
      </button>
    </form>
  );
}

export default function PublishPaymentScreen({ payment, onSuccess, onCancel }) {
  const [pk, setPk] = useState(null);
  // Required Terms acceptance. Every Stripe payment surface — both the
  // Payment Element and Express Checkout (Apple Pay / Google Pay) — lives
  // INSIDE `<Elements>`, and `<Elements>` is only mounted after this flag
  // flips to true. That is why Express Checkout cannot bypass acceptance
  // even though its confirm handler is registered by Stripe rather than us.
  const [accepted, setAccepted] = useState(false);
  useEffect(() => {
    api.get("/payments/config").then((r) => setPk(r.data.publishable_key)).catch(() => {});
  }, []);
  const options = useMemo(() => payment?.clientSecret ? {
    clientSecret: payment.clientSecret,
    appearance: { theme: "stripe", variables: { colorPrimary: "#B4322A", fontFamily: "Manrope, sans-serif" } },
  } : null, [payment]);

  return (
    <div className="max-w-md mx-auto px-6 sm:px-10 pt-16 pb-24" data-testid="publish-payment-screen">
      <div className="oh-label">Payment</div>
      <h1 className="font-serif-editorial text-4xl sm:text-5xl mt-4 leading-[1.02]">
        €1 · One human. One voice.
      </h1>
      <p className="text-[color:var(--oh-ink-2)] mt-4 text-sm leading-relaxed">
        Your €1 publishes “{payment.title}” and adds you as Voice #1.
      </p>

      <TermsAcceptance
        accepted={accepted}
        onChange={setAccepted}
        mode="paid"
        testId="publish-terms"
      />

      <div className="mt-8">
        {accepted && pk && options ? (
          <Elements stripe={getStripe(pk)} options={options}>
            <Inner providerIntentId={payment.providerIntentId} onDone={onSuccess} />
          </Elements>
        ) : (
          <div className="oh-label" data-testid="publish-payment-locked">
            {accepted ? "Preparing checkout…" : "Accept the Terms to continue"}
          </div>
        )}
      </div>
      <button onClick={onCancel}
              className="btn-ghost w-full mt-6" data-testid="publish-cancel">
        Not now
      </button>
      <p className="mt-6 text-[10px] uppercase tracking-widest text-[color:var(--oh-ink-3)]"
         data-testid="publish-merchant">
        Secure payment by Stripe ·{" "}
        <a href="/terms" target="_blank" rel="noopener noreferrer"
           className="underline underline-offset-2 hover:text-[color:var(--oh-ink)]"
           data-testid="publish-terms-link">
          Terms
        </a>
        {" · "}
        <a href="/privacy" target="_blank" rel="noopener noreferrer"
           className="underline underline-offset-2 hover:text-[color:var(--oh-ink)]"
           data-testid="publish-privacy-link">
          Privacy
        </a>
      </p>
    </div>
  );
}
